import { ref, type Ref } from 'vue';
import { departmentApi } from '@/entities/Department';
import { downloadBlobFile, handleApiError, useCustomToast, type SortOrder } from '@/shared/lib';

type UseDepartmentsExportParams = {
  searchTerm: Ref<string>;
  sort: Ref<string>;
  order: Ref<SortOrder>;
};

export const useDepartmentsExport = ({ searchTerm, sort, order }: UseDepartmentsExportParams) => {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const { blob, exportedCount, filename } = await departmentApi.exportDepartmentsExcel({
        search: searchTerm.value.trim() || undefined,
        sort: sort.value || undefined,
        order: order.value || undefined,
      });

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1
          ? '1 departamento exportado'
          : `${exportedCount} departamentos exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, 'Tente novamente.');
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExporting,
  };
};
